/**
 * OperatingModelControl - ops sets whether a tenant is a school, an owner
 * collecting its own rent, or an agency collecting for owners.
 *
 * Tenants cannot change this themselves (onboarding redesign, decision 10):
 * making a rental tenant an agency switches on trust accounting, and rent
 * already settled is not re-split. So a change needs a reason and is audited.
 */
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  OPERATING_MODEL_LABELS,
  adminService,
  operatingModelsFor,
  type OperatingModel,
  type TenantDetail,
} from '../services/admin'
import { LoadingButton } from './LoadingButton'
import { apiErrorMessage } from '@lipaflow/shared'

const MIN_REASON = 10

interface Props {
  tenant: TenantDetail
  onChanged: (tenant: TenantDetail) => void
}

export function OperatingModelControl({ tenant, onChanged }: Props) {
  const options = operatingModelsFor(tenant.vertical_code)
  const [model, setModel] = useState<OperatingModel | null>(tenant.operating_model)
  const [reason, setReason] = useState('')

  const save = useMutation({
    mutationFn: () => adminService.setOperatingModel(tenant.id, model as OperatingModel, reason.trim()),
    onSuccess: (updated) => {
      toast.success('Operating model updated')
      setReason('')
      onChanged(updated)
    },
    onError: (err: unknown) => {
      toast.error(apiErrorMessage(err, 'Failed to update the operating model'))
    },
  })

  if (options.length === 0) {
    return (
      <section aria-labelledby="operating-model-heading">
        <h4 id="operating-model-heading" className="font-inter text-caption text-stone-500">
          Operating model
        </h4>
        <p className="font-inter text-body-sm text-stone-600">
          Not applicable to this vertical.
        </p>
      </section>
    )
  }

  const changed = model !== tenant.operating_model
  const becomingAgency = changed && model === 'agency_managed'
  const leavingAgency = changed && tenant.operating_model === 'agency_managed'
  const canSave = changed && model !== null && reason.trim().length >= MIN_REASON

  return (
    <section aria-labelledby="operating-model-heading" className="space-y-3 rounded-xl border border-stone-200 p-4">
      <div>
        <h4 id="operating-model-heading" className="font-inter text-body font-medium text-stone-800">
          Operating model
        </h4>
        <p className="font-inter text-body-sm text-stone-500">
          Currently: {tenant.operating_model ? OPERATING_MODEL_LABELS[tenant.operating_model] : 'Not set'}
        </p>
      </div>

      {(options.length > 1 || tenant.operating_model === null) && (
        <div>
          <label htmlFor="tenant-operating-model" className="mb-1 block text-sm font-medium text-stone-700">
            Change to
          </label>
          <select
            id="tenant-operating-model"
            value={model ?? ''}
            onChange={(e) => setModel((e.target.value || null) as OperatingModel | null)}
            className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
          >
            {tenant.operating_model === null && <option value="">Not set</option>}
            {options.map((option) => (
              <option key={option} value={option}>
                {OPERATING_MODEL_LABELS[option]}
              </option>
            ))}
          </select>
        </div>
      )}

      {becomingAgency && (
        <p role="note" className="rounded-md bg-warning-50 px-3 py-2 text-sm text-warning-800">
          Rent settled from now on is split between each owner and the agency&apos;s commission.
          Rent already collected stays booked as it is.
        </p>
      )}
      {leavingAgency && (
        <p role="note" className="rounded-md bg-warning-50 px-3 py-2 text-sm text-warning-800">
          New rent stops being split for owners. Check for payouts still waiting for approval first.
        </p>
      )}

      {changed && (
        <div>
          <label htmlFor="operating-model-reason" className="mb-1 block text-sm font-medium text-stone-700">
            Reason (kept in the audit log)
          </label>
          <textarea
            id="operating-model-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="e.g. Confirmed EARB licence; manages 40 units for 6 owners"
            className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
          />
          <div className="mt-2 flex justify-end">
            <LoadingButton
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={!canSave}
              loadingText="Saving..."
              size="sm"
            >
              Save operating model
            </LoadingButton>
          </div>
        </div>
      )}
    </section>
  )
}
